/* Infrared Shower v2 — interactions. Everything degrades gracefully without JS. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Header: measure height (hero sits under it on the homepage) + solid state once scrolled */
  const initHeader = () => {
    const head = document.querySelector('.section-header');
    const setH = () => head && document.documentElement.style.setProperty('--ir-head-h', head.offsetHeight + 'px');
    setH();
    window.addEventListener('resize', setH);
    const onScroll = () => document.body.classList.toggle('ir-scrolled', window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  };

  /* Fade-up reveals; children of [data-ir-stagger] get increasing delays */
  const initReveal = (root) => {
    root.querySelectorAll('[data-ir-stagger]').forEach((group) => {
      [...group.querySelectorAll('[data-ir-reveal]')].forEach((el, i) => el.style.setProperty('--d', (i * 0.08).toFixed(2) + 's'));
    });
    const els = root.querySelectorAll('[data-ir-reveal]:not(.is-in)');
    if (!els.length) return;
    if (reduce || !('IntersectionObserver' in window)) { els.forEach((e) => e.classList.add('is-in')); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    // Above the fold reveals straight away (hero copy must never wait on the observer).
    els.forEach((e) => { if (e.getBoundingClientRect().top < window.innerHeight) e.classList.add('is-in'); else io.observe(e); });
  };

  /* Looping videos play only while visible */
  const initVideos = (root) => {
    const vids = root.querySelectorAll('video.ir-autoplay');
    if (!vids.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) { const p = target.play(); if (p && p.catch) p.catch(() => {}); } else target.pause();
      });
    }, { threshold: 0.2 });
    vids.forEach((v) => { v.muted = true; io.observe(v); });
    // <source media> is only read on load, so swap desktop/phone files when the width crosses a breakpoint
    // (rotating a tablet, or the theme editor switching to its mobile preview).
    vids.forEach((v) => {
      const sources = [...v.querySelectorAll('source[media]')];
      if (!sources.length || v.dataset.irSwap) return;
      v.dataset.irSwap = '1';
      const pick = () => {
        const s = [...v.querySelectorAll('source')].find((el) => !el.media || window.matchMedia(el.media).matches);
        if (!s || v.currentSrc === s.src) return;
        v.src = s.src;
        const p = v.play(); if (p && p.catch) p.catch(() => {});
      };
      sources.forEach((el) => window.matchMedia(el.media).addEventListener('change', pick));
    });
  };

  /* Carousels: [data-ir-carousel] > .ir-track + [data-ir-prev]/[data-ir-next] + [data-ir-dots] */
  const initCarousels = (root) => {
    root.querySelectorAll('[data-ir-carousel]').forEach((wrap) => {
      if (wrap.dataset.irReady) return;
      wrap.dataset.irReady = '1';
      const track = wrap.querySelector('.ir-track');
      if (!track) return;
      const cards = [...track.children];
      const pad = () => parseFloat(getComputedStyle(track).paddingLeft) || 0;
      const left = (c) => c.offsetLeft - track.offsetLeft - pad();
      const step = () => (cards[1] ? cards[1].offsetLeft - cards[0].offsetLeft : track.clientWidth);
      wrap.querySelector('[data-ir-prev]')?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      wrap.querySelector('[data-ir-next]')?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      const dots = wrap.querySelector('[data-ir-dots]');
      if (dots && cards.length > 1) {
        cards.forEach((c, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
          b.addEventListener('click', () => track.scrollTo({ left: left(c), behavior: 'smooth' }));
          dots.appendChild(b);
        });
        const update = () => {
          let best = 0, bestD = Infinity;
          cards.forEach((c, i) => { const d = Math.abs(left(c) - track.scrollLeft); if (d < bestD) { bestD = d; best = i; } });
          [...dots.children].forEach((b, i) => b.setAttribute('aria-current', i === best ? 'true' : 'false'));
        };
        track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
        window.addEventListener('resize', update);
        update();
      }
    });
  };

  /* Count-up numbers: "48%" -> 0..48% when scrolled into view */
  const initCount = (root) => {
    const nums = root.querySelectorAll('[data-ir-count]');
    if (!nums.length) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) return;
        io.unobserve(target);
        const raw = target.dataset.irCount;
        const m = raw.match(/^(\D*)(\d+(?:[.,]\d+)?)(.*)$/);
        if (!m || reduce) return;
        const [, pre, numStr, post] = m;
        const comma = numStr.includes(',');
        const end = parseFloat(numStr.replace(',', ''));
        const dec = comma ? 0 : (numStr.split('.')[1] || '').length;
        const t0 = performance.now();
        const tick = (t) => {
          const k = Math.min(1, (t - t0) / 1600);
          const v = end * (1 - Math.pow(1 - k, 3));
          target.textContent = pre + (comma ? Math.round(v).toLocaleString('en-US') : v.toFixed(dec)) + post;
          if (k < 1) requestAnimationFrame(tick); else target.textContent = raw;
        };
        requestAnimationFrame(tick);
      });
    }, { threshold: 0.5 });
    nums.forEach((n) => io.observe(n));
  };

  /* Statement: words light up as the paragraph scrolls through the viewport */
  const initStatement = (root) => {
    root.querySelectorAll('[data-ir-words]').forEach((el) => {
      if (el.dataset.irReady) return;
      el.dataset.irReady = '1';
      const walk = (node) => {
        [...node.childNodes].forEach((n) => {
          if (n.nodeType === 3) {
            const frag = document.createDocumentFragment();
            n.textContent.split(/(\s+)/).forEach((part) => {
              if (!part) return;
              if (/^\s+$/.test(part)) frag.appendChild(document.createTextNode(part));
              else { const s = document.createElement('span'); s.className = 'ir-word'; s.textContent = part; frag.appendChild(s); }
            });
            n.replaceWith(frag);
          } else if (n.nodeType === 1) walk(n);
        });
      };
      walk(el);
      const words = [...el.querySelectorAll('.ir-word')];
      if (reduce) { words.forEach((w) => w.classList.add('is-lit')); return; }
      const update = () => {
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        const p = Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (r.height + vh * 0.35)));
        const lit = Math.round(p * words.length);
        words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
      };
      update();
      window.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    });
  };

  /* Sticky-scroll steps: the step nearest the middle of the screen is active; media follows */
  const initSteps = (root) => {
    root.querySelectorAll('[data-ir-steps]').forEach((sec) => {
      const steps = [...sec.querySelectorAll('[data-ir-step]')];
      const media = [...sec.querySelectorAll('[data-ir-step-media]')];
      if (!steps.length) return;
      const set = (i) => {
        steps.forEach((s, n) => s.classList.toggle('is-active', n === i));
        media.forEach((m) => m.classList.toggle('is-active', Number(m.dataset.irStepMedia) === i || (media.length === 1)));
        const bar = sec.querySelector('[data-ir-progress]');
        if (bar) bar.style.transform = 'scaleY(' + ((i + 1) / steps.length) + ')';
      };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) set(steps.indexOf(e.target)); });
      }, { rootMargin: '-45% 0px -45% 0px', threshold: 0 });
      steps.forEach((s) => io.observe(s));
      set(0);
    });
  };

  /* Sticky add-to-cart: on the product page it appears once the main button scrolls away */
  const initSticky = () => {
    const bar = document.querySelector('[data-ir-sticky]');
    if (!bar) return;
    const main = document.querySelector('#MainContent [id^="ProductSubmitButton-"]');
    if (main) {
      const io = new IntersectionObserver(([e]) => bar.classList.toggle('is-visible', !e.isIntersecting && e.boundingClientRect.top < 0));
      io.observe(main);
    } else {
      const after = Number(bar.dataset.irShowAfter) || 700;
      const on = () => bar.classList.toggle('is-visible', window.scrollY > after);
      on();
      window.addEventListener('scroll', on, { passive: true });
    }
    bar.querySelector('[data-ir-sticky-btn]')?.addEventListener('click', () => {
      if (main && !main.disabled) main.click();
      else document.querySelector('#MainContent')?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  /* Expert / long-form videos: tap to play with sound */
  const initPlayers = (root) => {
    root.querySelectorAll('[data-ir-play]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const box = btn.closest('[data-ir-player]');
        const v = box && box.querySelector('video');
        if (!v) return;
        v.controls = true; v.muted = false; v.play(); box.classList.add('is-playing');
      });
    });
  };

  const init = (root = document) => {
    initReveal(root); initVideos(root); initCarousels(root); initCount(root); initStatement(root); initSteps(root); initPlayers(root);
  };
  const boot = () => { initHeader(); init(); initSticky(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
